import React from "react";
import { toast, ToastContent, ToastOptions } from "react-toastify";

interface CustomToastProps {
  title: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
}

const CustomToast: React.FC<CustomToastProps> = ({ title, message, type }) => {
  const timestamp = new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const getIcon = () => {
    switch (type) {
      case "success":
        return "✓";
      case "error":
        return "✕";
      case "warning":
        return "⚠";
      case "info":
        return "ℹ";
      default:
        return "•";
    }
  };

  const getColors = () => {
    switch (type) {
      case "success":
        return { bg: "#10b981", border: "#059669" };
      case "error":
        return { bg: "#ef4444", border: "#dc2626" };
      case "warning":
        return { bg: "#f59e0b", border: "#d97706" };
      case "info":
        return { bg: "#3b82f6", border: "#2563eb" };
      default:
        return { bg: "#6b7280", border: "#4b5563" };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        background: `linear-gradient(135deg, ${colors.bg}ee 0%, ${colors.bg}dd 100%)`,
        backdropFilter: "blur(12px)",
        border: `1px solid ${colors.border}40`,
        borderRadius: "12px",
        padding: "12px 16px",
        color: "white",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "3px" }}>
        <div style={{ fontSize: "15px", fontWeight: "600", letterSpacing: "0.025em" }}>
          {title}
        </div>
        <div style={{ fontSize: "12px", opacity: 0.75, marginLeft: "12px", whiteSpace: "nowrap" }}>
          {timestamp}
        </div>
      </div>
      <div style={{ fontSize: "14px", opacity: 0.95, lineHeight: "1.4" }}>
        {message}
      </div>
    </div>
  );
};

export const showCustomToast = (
  title: string,
  message: string,
  type: "success" | "error" | "warning" | "info",
  toastId?: string
) => {
  const options: ToastOptions = {
    position: "top-right",
    autoClose: 4000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: false,
    pauseOnFocusLoss: false,
    draggable: true,
    toastId: toastId || `${title}-${message}`,
    icon: false,
    closeButton: false,
    style: {
      background: "transparent",
      boxShadow: "none",
      padding: 0,
      margin: 0,
      borderRadius: "12px",
      overflow: "hidden",
    },
  };

  const content: ToastContent = (
    <CustomToast title={title} message={message} type={type} />
  );

  switch (type) {
    case "success":
      toast.success(content, options);
      break;
    case "error":
      toast.error(content, options);
      break;
    case "warning":
      toast.warning(content, options);
      break;
    case "info":
      toast.info(content, options);
      break;
  }
};

// Utility functions for common toast messages
export const showSystemOnline = (systemName: string) => {
  showCustomToast(
    "System Online",
    `${systemName} is now online!`,
    "info",
    `online-${systemName}`
  );
};

export const showSystemOffline = (systemName: string) => {
  showCustomToast(
    "System Offline",
    `${systemName} is now offline!`,
    "error",
    `offline-${systemName}`
  );
};

export const showCheckReminder = (systemName: string) => {
  showCustomToast(
    "Check Reminder",
    `Check due for ${systemName}`,
    "info",
    `reminder-${systemName}`
  );
};

export const showCheckMissed = (systemName: string, claimedBy: string) => {
  showCustomToast(
    "Check Missed",
    `${systemName} - claimed by ${claimedBy}`,
    "error",
    `missed-${systemName}`
  );
};

export const showInvalidHeadbox = (systemName: string) => {
  showCustomToast(
    "Invalid Headbox",
    `${systemName} has an invalid headbox configuration`,
    "warning",
    `invalid-${systemName}`
  );
};

export const showNeuroworksClosed = (systemName: string) => {
  showCustomToast(
    "NeuroWorks Closed",
    `${systemName} has no active study`,
    "warning",
    `nw-closed-${systemName}`
  );
};

export default CustomToast;
